import { h } from '@omni-torn/ui';
import { capacity, capacityPerks, dayPlan, DEFAULT_AGENCY, departure, emptyAt, flightText, stockAt, type Confidence, type FlightType, type PlanOption, type RestockModel, type StockPoint } from '@omni-torn/shared/abroad';
import type { RefItem, TornRefAnswer } from '@omni-torn/shared/torn-ref';
import { TRAVEL_TYPES, type TravelType } from '@omni-torn/shared/travel';
import { setAddressQuery } from '../log-kit/ui';
import { readTravel } from '../read';
import { server, torn } from '../session';
import { ago, box, duration, facts, lineChart, loading, money, num, problem, short, table, tabs, tct, withTableCsv } from '../ui';
import { renderTravelHistory } from './travel-history';

/**
 * Travel (website roadmap S10: Torn Intel's abroad stock, weav3r's Travel Stock, TornStats' travel profits): your flight;
 * what every country's shops have now (pooled from what Omni-Torn users saw abroad — anonymous, `GET
 * /api/travel/forecast`), worth back home (Torn's market values, `/api/torn/items`), profit per item, trip and hour for
 * your flight and capacity; each item's restock history (1–48 h) and forecast with a confidence; when to leave
 * (departure planner) and a day plan of trips back to back (packages/shared abroad.ts). Torn is asked only for your
 * flight and your perks (capacity), with your key; the rest comes from the Omni-Torn server.
 * History (roadmap S29, pages/travel-history.ts): your trips, buys abroad and flights per day from your own log.
 */

const TYPE_LABEL: Record<TravelType, string> = { standard: 'Standard', airstrip: 'Airstrip', wlt: 'WLT business', business: 'Business class' };
const FLIGHT_OF: Record<TravelType, FlightType> = { standard: 'standard', airstrip: 'airstrip', wlt: 'private', business: 'business' };
const TYPE_KEY = 'omni-torn.travelType';
const OPTS_KEY = 'omni-torn.travelAbroad';
const REFRESH_MS = 60_000;
const MIN = 60_000;
const HOUR = 60 * MIN;

interface AbroadItem {
  id: number;
  name: string | null;
  quantity: number;
  cost: number;
  seenAt: number;
  model: RestockModel;
  last: StockPoint | null;
}
interface Forecast {
  at: number;
  countries: Record<string, { update: number; items: AbroadItem[] }>;
}

interface Row {
  key: string;
  code: string;
  country: string;
  item: AbroadItem;
  name: string;
  kind: string | null;
  value: number | null;
  each: number | null;
  amount: number;
  trip: number | null;
  perHour: number | null;
  oneWayMs: number;
  ticket: number;
}

interface Opts {
  extra: number;
  fee: number;
  country: string;
  kind: string;
  inStock: boolean;
  hours: number;
  planHours: number;
}
const DEFAULT_OPTS: Opts = { extra: 0, fee: 0, country: '', kind: '', inStock: false, hours: 24, planHours: 8 };

function loadOpts(): Opts {
  try {
    const o = JSON.parse(localStorage.getItem(OPTS_KEY) ?? '{}') as Partial<Opts>;
    return { ...DEFAULT_OPTS, ...Object.fromEntries(Object.entries(o).filter(([k, v]) => k in DEFAULT_OPTS && typeof v === typeof DEFAULT_OPTS[k as keyof Opts])) };
  } catch {
    return { ...DEFAULT_OPTS };
  }
}
function saveOpts(o: Opts): void {
  try {
    localStorage.setItem(OPTS_KEY, JSON.stringify(o));
  } catch {
    /* this visit only */
  }
}

const CONF_TEXT: Record<Confidence, string> = { none: 'no restock seen', low: 'low confidence', medium: 'medium confidence', high: 'high confidence' };
const confBadge = (c: Confidence) => (c === 'none' ? null : h('span', { class: `badge conf-${c}`, title: CONF_TEXT[c] }, c));
const signedMoney = (n: number | null) => (n === null ? '—' : h('span', { class: n >= 0 ? 't-green' : 't-red' }, `${n < 0 ? '−' : ''}$${short(Math.abs(n))}`));

/** "~14:20 TCT (13:55–14:45)", or why there's no forecast yet. */
function nextText(m: RestockModel, now: number): string {
  if (!m.next) return m.restocks ? 'one restock seen' : 'none seen yet';
  const n = m.next;
  return n.at <= now ? 'due now' : `~${tct(n.at)} (${tct(n.early)}–${tct(n.late)})`;
}

type Tab = 'now' | 'history';

export async function renderTravel(view: HTMLElement): Promise<void> {
  let tab: Tab = new URLSearchParams(location.search).get('tab') === 'history' ? 'history' : 'now';
  const pane = h('div', {});
  const show = () => {
    const body = h('div', { 'data-travel-tab': tab });
    pane.replaceChildren(body);
    void (tab === 'history' ? renderTravelHistory(body) : renderNow(body));
  };
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Travel')),
    tabs(
      [
        { id: 'now', label: 'Now' },
        { id: 'history', label: 'History' },
      ],
      tab,
      (id) => {
        tab = id === 'history' ? 'history' : 'now';
        setAddressQuery(null, tab === 'history' ? { tab } : { tab: null, range: null, from: null, to: null, country: null });
        show();
      },
    ),
    pane,
  );
  show();
}

/** The Now tab: your flight, the stock abroad, an item's forecast, the day plan, flight times. */
async function renderNow(body: HTMLElement): Promise<void> {
  body.replaceChildren(loading('Reading your flight…'));
  let tr;
  try {
    tr = readTravel(await torn('/user/', { selections: 'travel' }));
  } catch (err) {
    body.replaceChildren(problem(err));
    return;
  }
  let type: TravelType = 'standard';
  try {
    const t = localStorage.getItem(TYPE_KEY);
    if (t && t in TRAVEL_TYPES) type = t as TravelType;
  } catch {
    /* default */
  }
  const opts = loadOpts();
  const setType = (t: TravelType) => {
    type = t;
    try {
      localStorage.setItem(TYPE_KEY, type);
    } catch {
      /* this visit only */
    }
    drawTimes();
    drawAbroad();
  };

  // ---------------------------------------------------------------- flight times (every destination)
  const timesBox = h('div', {});
  const drawTimes = () => {
    const now = Date.now();
    timesBox.replaceChildren(
      tabs(
        (Object.keys(TRAVEL_TYPES) as TravelType[]).map((t) => ({ id: t, label: TYPE_LABEL[t] })),
        type,
        (id) => setType(id as TravelType),
      ),
      withTableCsv(h(
        'div',
        { class: 'table-wrap' },
        h(
          'table',
          {},
          h('thead', {}, h('tr', {}, h('th', {}, 'Destination'), h('th', { class: 'r' }, 'One way'), h('th', { class: 'r' }, 'Land (leave now)'), h('th', { class: 'r' }, 'Back (straight back)'))),
          h(
            'tbody',
            {},
            // Torn's travel agency times (the same as the profits above use).
            ...DEFAULT_AGENCY.countries.map((c) => {
              const sec = c.seconds[flight()] ?? 0;
              return h('tr', {}, h('td', {}, c.name), h('td', { class: 'r' }, duration(sec)), h('td', { class: 'r' }, `${tct(now + sec * 1000)} TCT`), h('td', { class: 'r' }, `${tct(now + 2 * sec * 1000)} TCT`));
            }),
          ),
        ),
      )),
    );
  };

  // ---------------------------------------------------------------- abroad stock, history, day plan
  let forecast: Forecast | null = null;
  let forecastProblem: unknown = null;
  let items = new Map<number, RefItem>();
  let perks = 0;
  let perksRead = false;
  let picked: string | null = null;
  const abroadBox = h('div', {}, loading('Reading the stock abroad…'));
  const itemBox = h('div', {});
  const planBox = h('div', {});

  const flight = () => FLIGHT_OF[type];
  const cap = () => Math.max(1, capacity(flight(), perks) + opts.extra);

  const rows = (): Row[] => {
    if (!forecast) return [];
    const out: Row[] = [];
    for (const c of DEFAULT_AGENCY.countries) {
      const shop = forecast.countries[c.code];
      if (!shop) continue;
      const sec = c.seconds[flight()] ?? null;
      const ticket = flight() === 'standard' ? (c.ticket ?? 0) : 0;
      for (const it of shop.items) {
        const ref = items.get(it.id);
        const value = ref?.marketValue ?? null;
        const each = value === null ? null : Math.round(value * (1 - opts.fee / 100) - it.cost);
        const amount = Math.max(0, Math.min(cap(), it.quantity));
        const trip = each === null ? null : Math.round(each * amount - ticket);
        out.push({ key: `${c.code}:${it.id}`, code: c.code, country: c.name, item: it, name: it.name ?? ref?.name ?? `Item ${it.id}`, kind: ref?.type ?? null, value, each, amount, trip, perHour: trip !== null && sec ? Math.round(trip / ((2 * sec) / 3600)) : null, oneWayMs: (sec ?? 0) * 1000, ticket });
      }
    }
    return out;
  };

  const control = (label: string, input: HTMLElement) => h('label', { class: 'field-inline' }, h('span', {}, label), input);
  const numberInput = (value: number, min: number, max: number, onSet: (n: number) => void, cls: string) => {
    const i = h('input', { type: 'number', min: String(min), max: String(max), value: String(value), class: cls, inputmode: 'numeric' }) as HTMLInputElement;
    i.addEventListener('change', () => {
      const n = Math.max(min, Math.min(max, Math.round(Number(i.value) || 0)));
      i.value = String(n);
      onSet(n);
    });
    return i;
  };

  const drawAbroad = () => {
    if (!forecast) {
      abroadBox.replaceChildren(forecastProblem ? problem(forecastProblem) : loading('Reading the stock abroad…'));
      planBox.replaceChildren();
      return;
    }
    const now = Date.now();
    const all = rows();
    const kinds = [...new Set(all.map((r) => r.kind).filter((k): k is string => !!k))].sort();
    const shown = all.filter((r) => (!opts.country || r.code === opts.country) && (!opts.kind || r.kind === opts.kind) && (!opts.inStock || r.item.quantity > 0));

    const flightSel = h('select', { class: 'abroad-flight' }, ...(Object.keys(TRAVEL_TYPES) as TravelType[]).map((t) => h('option', { value: t, selected: t === type }, TYPE_LABEL[t]))) as HTMLSelectElement;
    flightSel.addEventListener('change', () => setType(flightSel.value as TravelType));
    const countrySel = h('select', { class: 'abroad-country' }, h('option', { value: '' }, 'Every country'), ...DEFAULT_AGENCY.countries.map((c) => h('option', { value: c.code, selected: c.code === opts.country }, c.name))) as HTMLSelectElement;
    countrySel.addEventListener('change', () => {
      opts.country = countrySel.value;
      saveOpts(opts);
      drawAbroad();
    });
    const kindSel = h('select', { class: 'abroad-kind' }, h('option', { value: '' }, 'Any kind'), ...kinds.map((k) => h('option', { value: k, selected: k === opts.kind }, k))) as HTMLSelectElement;
    kindSel.addEventListener('change', () => {
      opts.kind = kindSel.value;
      saveOpts(opts);
      drawAbroad();
    });
    const inStock = h('input', { type: 'checkbox', class: 'abroad-instock', checked: opts.inStock }) as HTMLInputElement;
    inStock.addEventListener('change', () => {
      opts.inStock = inStock.checked;
      saveOpts(opts);
      drawAbroad();
    });

    const leaveCell = (r: Row) => {
      if (!r.oneWayMs) return '—';
      const d = departure(r.item.model, r.item.last, now, r.oneWayMs, Math.min(cap(), r.item.model.peak ?? cap()));
      if (!d) return h('span', { class: 'muted' }, r.item.model.interval ? 'not in 24 h' : 'wait for a restock');
      return d.waitMin <= 0 ? h('span', { class: 't-green', title: `Land ${tct(d.landAt)} TCT with ~${num(d.stock)} there` }, 'Leave now') : h('span', { title: `Land ${tct(d.landAt)} TCT with ~${num(d.stock)} there` }, `${tct(d.leaveAt)} (in ${flightText(d.waitMin * 60)})`);
    };
    const nameCell = (r: Row) => {
      const b = h('button', { type: 'button', class: `abroad-pick${picked === r.key ? ' on' : ''}`, 'data-key': r.key, title: 'History and forecast' }, r.name);
      b.addEventListener('click', () => {
        picked = r.key;
        drawItem();
        itemBox.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
      return b;
    };

    const seen = Object.values(forecast.countries).map((c) => c.update);
    abroadBox.replaceChildren(
      h(
        'div',
        { class: 'toolbar' },
        control('Flight', flightSel),
        control(
          'Extra items',
          numberInput(opts.extra, -20, 100, (n) => {
            opts.extra = n;
            saveOpts(opts);
            drawAbroad();
          }, 'abroad-extra'),
        ),
        control(
          'Selling fee %',
          numberInput(opts.fee, 0, 50, (n) => {
            opts.fee = n;
            saveOpts(opts);
            drawAbroad();
          }, 'abroad-fee'),
        ),
        control('Country', countrySel),
        control('Kind', kindSel),
        h('label', { class: 'field-inline' }, inStock, h('span', {}, 'In stock only')),
      ),
      h('p', { class: 'muted small abroad-cap' }, `You carry ${cap()} items on a ${TYPE_LABEL[type]} flight${perksRead ? ` (${perks ? `+${perks} from your perks` : 'no travel perks read'})` : ' (your perks couldn’t be read — add them as extra items)'}${opts.extra ? `, ${opts.extra > 0 ? '+' : ''}${opts.extra} you added` : ''}.`),
      !all.length
        ? h('p', { class: 'muted' }, 'No shop abroad seen yet. Stock shows here once an Omni-Torn user opens a shop abroad (sharing is on by default in the extension).')
        : table(
            shown,
            [
              { label: 'Country', get: (r) => r.country, sort: (r) => r.country },
              { label: 'Item', get: nameCell, sort: (r) => r.name },
              { label: 'Stock', get: (r) => h('span', { title: `Seen ${ago(r.item.seenAt)}` }, num(r.item.quantity), h('span', { class: 'muted small' }, ` · ${ago(r.item.seenAt)}`)), sort: (r) => r.item.quantity, cls: 'r' },
              { label: 'Cost', get: (r) => money(r.item.cost), sort: (r) => r.item.cost, cls: 'r' },
              { label: 'Value', get: (r) => money(r.value), sort: (r) => r.value ?? -1, cls: 'r' },
              { label: 'Profit / item', get: (r) => signedMoney(r.each), sort: (r) => r.each ?? -1e15, cls: 'r' },
              { label: 'Profit / trip', get: (r) => h('span', { title: `${r.amount} items${r.ticket ? `, less the ${money(r.ticket)} ticket` : ''}` }, signedMoney(r.trip)), sort: (r) => r.trip ?? -1e15, cls: 'r' },
              { label: 'Profit / hour', get: (r) => signedMoney(r.perHour), sort: (r) => r.perHour ?? -1e15, cls: 'r' },
              { label: 'Next restock', get: (r) => h('span', {}, nextText(r.item.model, now), ' ', confBadge(r.item.model.confidence)), sort: (r) => r.item.model.next?.at ?? 1e15 },
              { label: 'Leave', get: leaveCell },
            ],
            { initial: 7, remember: 'travel-abroad', empty: 'Nothing matches these filters.' },
          ),
      h('p', { class: 'muted small' }, `Pooled from what Omni-Torn users saw in shops abroad (newest ${seen.length ? ago(Math.max(...seen)) : '—'}); values are Torn’s market values${opts.fee ? ` less your ${opts.fee}% fee` : ''}; a standard flight pays its ticket. Times in TCT. “Leave” is when to fly to land with a full load, from the restock forecast.`),
    );
    drawItem();
    drawPlan();
  };

  const drawItem = () => {
    const r = picked ? rows().find((x) => x.key === picked) : null;
    if (!r) return itemBox.replaceChildren(h('p', { class: 'muted' }, 'Pick an item in the list to see its stock over time and when it restocks.'));
    const now = Date.now();
    const m = r.item.model;
    const chart = h('div', {}, loading('Reading its history…'));
    const hourTabs = tabs(
      [1, 6, 12, 24, 48].map((x) => ({ id: String(x), label: `${x} h` })),
      String(opts.hours),
      (id) => {
        opts.hours = Number(id);
        saveOpts(opts);
        loadHistory();
      },
    );
    const loadHistory = async () => {
      try {
        const a = await server<{ points: StockPoint[] }>('GET', `/api/travel/history?country=${r.code}&item=${r.item.id}&hours=${opts.hours}`);
        const pts = (a.points ?? []).map((p) => ({ at: p.at, v: p.qty }));
        chart.replaceChildren(lineChart(pts, { label: `${r.name} in stock`, format: (n) => num(Math.round(n)), xFormat: (ms) => `${tct(ms)} TCT` }));
      } catch (err) {
        chart.replaceChildren(problem(err));
      }
    };
    const d = r.oneWayMs ? departure(m, r.item.last, now, r.oneWayMs, Math.min(cap(), m.peak ?? cap())) : null;
    const empty = emptyAt(m, r.item.last, now);
    const landNow = r.oneWayMs ? stockAt(m, r.item.last, now + r.oneWayMs) : null;
    itemBox.replaceChildren(
      h('h3', { class: 'abroad-item-title' }, `${r.name} · ${r.country}`),
      hourTabs,
      chart,
      facts([
        ['In stock', `${num(r.item.quantity)} (seen ${ago(r.item.seenAt)})`],
        ['Restocks seen (48 h)', String(m.restocks)],
        ['Restocks about every', m.interval ? flightText(m.interval / 1000) : '—'],
        ['Usual stock after one', m.peak === null ? '—' : num(m.peak)],
        ['Sells', m.drainPerHour === null ? '—' : `~${num(Math.round(m.drainPerHour))} an hour`],
        ['Runs out', empty ? `~${tct(empty)} TCT` : '—'],
        ['Next restock', h('span', {}, nextText(m, now), m.next ? ' TCT ' : ' ', confBadge(m.confidence))],
        ['If you leave now', landNow === null ? '—' : `land ${tct(now + r.oneWayMs)} TCT with ~${num(landNow)} there`],
        ['Best time to leave', !d ? '—' : d.waitMin <= 0 ? 'now' : `${tct(d.leaveAt)} TCT (land ${tct(d.landAt)} with ~${num(d.stock)} there)`],
      ]),
      h('p', { class: 'muted small' }, 'The forecast learns from the restocks Omni-Torn users saw: more sightings, more regular restocks → higher confidence.'),
    );
    void loadHistory();
  };

  const drawPlan = () => {
    if (!forecast) return;
    const now = Date.now();
    const options: PlanOption[] = rows()
      .filter((r) => r.each !== null && r.oneWayMs > 0 && (!opts.country || r.code === opts.country) && (!opts.kind || r.kind === opts.kind))
      .map((r) => ({ key: r.key, country: r.country, item: r.name, each: r.each!, ticket: r.ticket, oneWayMs: r.oneWayMs, model: r.item.model, last: r.item.last }));
    const trips = dayPlan(options, cap(), now, now + opts.planHours * HOUR);
    const hoursIn = numberInput(opts.planHours, 1, 24, (n) => {
      opts.planHours = n;
      saveOpts(opts);
      drawPlan();
    }, 'plan-hours');
    const total = trips.reduce((a, t) => a + t.profit, 0);
    planBox.replaceChildren(
      h('div', { class: 'toolbar' }, control('Hours you have', hoursIn), h('span', { class: 'muted small' }, `${TYPE_LABEL[type]} flight, ${cap()} items, the country and kind filters above.`)),
      !trips.length
        ? h('p', { class: 'muted' }, 'No profitable trip fits in that time with what’s known of the stock.')
        : withTableCsv(h(
            'div',
            { class: 'table-wrap' },
            h(
              'table',
              { class: 'day-plan' },
              h('thead', {}, h('tr', {}, h('th', {}, 'Leave'), h('th', {}, 'To'), h('th', {}, 'Buy'), h('th', { class: 'r' }, 'Land'), h('th', { class: 'r' }, 'Back'), h('th', { class: 'r' }, 'Profit'), h('th', {}, 'Forecast'))),
              h(
                'tbody',
                {},
                ...trips.map((t) =>
                  h('tr', {}, h('td', {}, t.leaveAt <= now + MIN ? h('strong', { class: 't-green' }, 'Now') : `${tct(t.leaveAt)} TCT`), h('td', {}, t.country), h('td', {}, `${t.amount} × ${t.item}`), h('td', { class: 'r' }, tct(t.landAt)), h('td', { class: 'r' }, tct(t.backAt)), h('td', { class: 'r' }, signedMoney(t.profit)), h('td', {}, confBadge(t.confidence))),
                ),
              ),
              h('tfoot', {}, h('tr', {}, h('td', { colspan: 5 }, `${trips.length} trip${trips.length === 1 ? '' : 's'}, home by ${tct(trips[trips.length - 1]!.backAt)} TCT`), h('td', { class: 'r' }, signedMoney(total)), h('td', {}))),
            ),
          )),
      h('p', { class: 'muted small' }, 'Each trip is the one that makes the most per hour from when you’re back (waiting for a restock counts as time). One item per trip; prices and stock can change while you fly.'),
    );
  };

  const loadForecast = async () => {
    try {
      forecast = await server<Forecast>('GET', '/api/travel/forecast');
      forecastProblem = null;
    } catch (err) {
      if (!forecast) forecastProblem = err;
    }
  };
  const loadItems = async () => {
    try {
      const a = await server<TornRefAnswer<'items'>>('GET', '/api/torn/items');
      items = new Map(a.data.map((i) => [i.id, i]));
    } catch {
      /* values show as — */
    }
  };
  const loadPerks = async () => {
    try {
      perks = capacityPerks(await torn('/user/', { selections: 'perks' }, 10 * MIN));
      perksRead = true;
    } catch {
      perksRead = false;
    }
  };

  drawTimes();
  body.replaceChildren(
    box(
      'Your flight',
      !tr
        ? h('p', { class: 'muted' }, '—')
        : tr.timeLeft > 0
          ? facts([
              ['Flying to', tr.destination],
              ['Left', tr.departed ? `${tct(tr.departed)} TCT` : '—'],
              ['Lands', tr.arrival ? `${tct(tr.arrival)} TCT (in ${duration(tr.timeLeft)})` : `in ${duration(tr.timeLeft)}`],
              ['Ticket', tr.method || '—'],
            ])
          : h('p', {}, tr.destination === 'Torn' ? 'You’re in Torn City.' : `You’re in ${tr.destination}.`),
    ),
    box('Stock abroad', abroadBox, { id: 'abroad' }),
    box('Item history & forecast', itemBox, { id: 'abroad-item' }),
    box('Day plan', planBox, { id: 'day-plan' }),
    box(
      'Flight times',
      [timesBox, h('p', { class: 'muted small' }, 'Times in TCT (Torn time = UTC).')],
      { id: 'flight-times' },
    ),
  );
  await Promise.all([loadForecast(), loadItems(), loadPerks()]);
  drawAbroad();

  // The pooled stock again every minute while this page is open and in view (the Omni-Torn server only, no Torn calls).
  const timer = setInterval(async () => {
    if (!body.isConnected) return clearInterval(timer);
    if (document.visibilityState !== 'visible') return;
    await loadForecast();
    if (body.isConnected) drawAbroad();
  }, REFRESH_MS);
}
